import { dataSource } from '../../Functions';
import { HardwareConfigRecord, DeviceConfig, PaletteRecord, HardwareConfig, DeviceTypes, PortTypeValues } from '../../config';
import { ChlorineSensorSensorTypes, getLightInfo, getCatalogEntryByDeviceConfig, ChlorineSensorCatalogItem, getPumpInfo, getHeaterInfo, getActuatorCatalogEntry } from '../../catalog';
import { safeObjCopy } from '../../utils/Json';
import Locale from "../../utils/Locale"
import * as Types from '../../status/Types';
import { findAllStates } from '../../status';
import { LightColorsAndShow, LightColors } from "../../catalog/documents/LightCatalog";

/**
 * Returns Device config.
 * If a uuid of a combind control was passed in then it returns the config of the first control.
 */
export const getDeviceByControl = function(controlUuid:string, config:HardwareConfigRecord) {
    let control;
    
    let isCombined = dataSource.isControlCombined({ UUID: controlUuid }, config)
    if (isCombined?.Controls && isCombined.Controls[0]) {
        control = dataSource.findEntity(isCombined.Controls[0].ControlUUID, 'Controls', config)?.entity;
    } else {
        control = dataSource.findEntity(controlUuid, 'Controls', config)?.entity;
    }
    
    if (control) {
        switch(control?.Type) {
            case "Light": return dataSource.findEntity(control.Light, 'Devices', config)?.entity; break;
        }
    } else {
        console.warn('getDeviceByControl() Could not found control by uuid:', controlUuid, 'isCombined:', isCombined, config);
    }
}

/**
 * @param lightEntity
 * @return
 */
export const getLightColors = function(lightEntity:DeviceConfig): LightColors[] | false {
    if (lightEntity?.HardwareDevice?.LightSettings) {
        const lightInfo = getLightInfo(lightEntity.HardwareDevice.LightSettings);
        if (lightInfo?.colorAndShowLibrary?.colorsAndShows?.length) {
            const lightColors = [] as LightColors[];
            let colors = JSON.parse(JSON.stringify(lightInfo.colorAndShowLibrary.colorsAndShows)) as LightColorsAndShow[];
            colors.forEach(item => {
                lightColors.push({
                    order: item.order,
                    name: item.name,
                    description: item.description,
                    isShow: item.isShow,
                    RGB: item.RGB,

                    uuid: item.name                
                })
            });
            if (lightInfo.colorAndShowLibrary.supportsCustomColorsAndShows) {
                const palette = safeObjCopy(dataSource.getCurrentConfig("Palette.json")) as PaletteRecord;
                if (palette?.data?.colors?.forEach) {
                    palette.data.colors.forEach(item => {
                        lightColors.push({
                            order: undefined,
                            // @ts-ignore
                            name: item.name || item.Name,
                            description: undefined,
                            
                            isShow: !!(
                                item.isShow === true || (
                                    ((item.isShow as unknown) as string)?.toLowerCase &&
                                    ((item.isShow as unknown) as string).toLowerCase() === 'true'
                                )
                            ),
                            RGB: item.colors.reduce((prev, curr, idx) => `${prev}${idx ? '-' : ''}${curr.RGB}`, ''),
                            uuid: item.uuid,
                        });
                    });
                }
            } else {
                //console.log("colorSelector", lightInfo.lightTechnology.colorModeSettings.colorSelector);
            }
            
            return lightColors;
        }
    }

    return false;
}

export function getChlorineSensorType (config: DeviceConfig): ChlorineSensorSensorTypes {
    const cataloEntry = getCatalogEntryByDeviceConfig(config) as ChlorineSensorCatalogItem;
    return cataloEntry?.sensorType;
}

/**
 * Returns the min count of ports for a given port type.
 * Count of HardwareDevice.Ports according to HardwareDevice.Ports[ 0 ].Type
 * NOTE: only for determine the min count of HardwareDevice.Ports array. Not for other port settings.
 * @param deviceConfig
 * @param AutoFilterVersion Controller's AutoFilterVersion.
 * @returns
 * @see getHardwareDevicePortsMaxCount()
 * @see getDevicePorts() for other config props related to port usage.
 */
export function getHardwareDevicePortsMinCount(deviceConfig: DeviceConfig, AutoFilterVersion?: number) {
    if ([ DeviceTypes.HEATER, DeviceTypes.HEATPUMP ].includes(deviceConfig.DeviceType)) {
        const deviceInfo = getHeaterInfo(deviceConfig);
        // Offer 2 ports right away if it is a relay only device.
        // Otherwise offer 2 ports only after relay was selected as port type
        if (
            (
                deviceInfo?.portTypesAvailable?.includes(PortTypeValues.HighVoltage) ||
                deviceInfo?.portTypesAvailable?.includes(PortTypeValues.LowVoltage)
            ) &&
            (   // It has only relay ports
                !deviceInfo?.portTypesAvailable?.includes(PortTypeValues.Serial) ||
                
                // It has serial port too
                deviceConfig.HardwareDevice?.Ports &&
                [ PortTypeValues.HighVoltage, PortTypeValues.LowVoltage ].includes(deviceConfig.HardwareDevice.Ports[0]?.Type)
            )
        ) {
            if (deviceConfig.HardwareDevice.HVACSettings?.SupportHeating && deviceConfig.HardwareDevice.HVACSettings?.SupportCooling) {
                return 2;
            }
        }
    } else if (deviceConfig.DeviceType === DeviceTypes.ACTUATORTHREEWAY) {
        if (AutoFilterVersion && AutoFilterVersion < 43) {
            // Controller below AutoFilterVersion 43 does not allow 3 way actuators with only 1 relay.
            // Force having 2 relays on for old Controllers.
            if (deviceConfig.HardwareDevice?.Ports[ 0 ]?.Type !== PortTypeValues.Actuator) {
                const deviceInfo = getActuatorCatalogEntry(deviceConfig.HardwareDevice.PrimaryCatalogId);
                if (deviceInfo?.portTypesAvailable.some(item => (
                    PortTypeValues.HighVoltage,
                    PortTypeValues.LowVoltage
                ).includes(item))) {
                    return 2;
                }
            }
        }
    }

    return 1;
}

/**
 * Returns the max count of ports for a given port type.
 * Count of HardwareDevice.Ports according to HardwareDevice.Ports[ 0 ].Type
 * NOTE: only for determine the max count of HardwareDevice.Ports array. Not for other port settings.
 * @param deviceConfig
 * @returns
 * @see getHardwareDevicePortsMinCount()
 * @see getDevicePorts() for other config props related to port usage.
 */
export function getHardwareDevicePortsMaxCount(deviceConfig: DeviceConfig) {
    let portCount = 1;
    
    if (deviceConfig.DeviceType == DeviceTypes.PUMP) {
        const pumpInfo = getPumpInfo(deviceConfig);
        portCount = (pumpInfo && pumpInfo['maxRelays']) ?? 0;
    } else if ([
        DeviceTypes.HEATER,
        DeviceTypes.HEATPUMP
    ].includes(deviceConfig.DeviceType)) {
        const deviceInfo = getHeaterInfo(deviceConfig);
        if (
            deviceConfig.HardwareDevice?.Ports[0]?.Type !== PortTypeValues.Serial
            && deviceInfo?.portTypesAvailable?.some(item => [
                PortTypeValues.HighVoltage,
                PortTypeValues.LowVoltage,
            ].includes(item))
        ) {
            portCount = 0;
            // @todo So this probably should work off of the catalog instead of config.
            if (deviceConfig.HardwareDevice.HVACSettings?.SupportHeating) {
                portCount++;
            }
            if (deviceConfig.HardwareDevice.HVACSettings?.SupportCooling) {
                portCount++;
            }
        }
    } else if ([
        DeviceTypes.ACTUATORTWOWAY,
        DeviceTypes.ACTUATORTHREEWAY
    ].includes(deviceConfig.DeviceType)) {
        if (deviceConfig.HardwareDevice?.Ports[ 0 ]?.Type !== PortTypeValues.Actuator) {
            const deviceInfo = getActuatorCatalogEntry(deviceConfig.HardwareDevice.PrimaryCatalogId);
            if (deviceInfo?.portTypesAvailable?.some(item => [
                PortTypeValues.HighVoltage,
                PortTypeValues.LowVoltage
            ].includes(item))) {
                // This means 2 relay ports and 0 valve ports
                portCount = 2;
            }
        }
    }

    return Math.max(1, portCount);
}

/**
 * Get all configurable ports for a given device according to it's config settings.
 * @param deviceConfig 
 * @see getDevicePorts()
 */
export function getAllPortTypes(deviceConfig: DeviceConfig) {
    const configProps = {};

    configProps[ 'HardwareDevice.Ports' ] = getHardwareDevicePortTypes(deviceConfig);
    
    if (deviceConfig.DeviceType === DeviceTypes.WATERLEVELSENSOR) {
        configProps[ 'HardwareDevice.WaterLevelSensorSettings.WaterLevelSensor' ] = [
            PortTypeValues.Switch,
            PortTypeValues.Thermistor
        ];
        configProps[ 'HardwareDevice?.WaterLevelSensorSettings.PoweringWaterLevelSensor' ] = [
            PortTypeValues.LowVoltage,
        ];
    }
    if ( deviceConfig.DeviceType === DeviceTypes.AUTOFILL) {
        configProps[ 'HardwareDevice.AutoFillSettings.SolenoidValve' ] = [
            PortTypeValues.LowVoltage,
            PortTypeValues.HighVoltage,
        ];
    }

    if (deviceConfig.EnablePowerPort) {
        configProps[ 'HardwareDevice?.PowerPort' ] = [
            PortTypeValues.HighVoltage,
        ]
    }

    return configProps;
}

/**
 * Get the primary connection port of configurable ports for a given device according to it's config settings.
 * @param deviceConfig 
 * @see getAllPortTypes()
 */
export function getPrimaryPortPortTypes(deviceConfig: DeviceConfig) {
    const path = getPrimaryPort(deviceConfig.DeviceType);
    return getAllPortTypes(deviceConfig)?.[ path ];
}

export function getPrimaryPort(deviceType: DeviceTypes) {
    switch (deviceType) {
    case DeviceTypes.WATERLEVELSENSOR: return 'HardwareDevice.WaterLevelSensorSettings.WaterLevelSensor';
    case DeviceTypes.AUTOFILL: return 'HardwareDevice.AutoFillSettings.SolenoidValve';
    default: return 'HardwareDevice.Ports';
    }
}

/**
 * Returns list of port types that can be configured as HardwreDevice.Ports for the given device.
 * @see getAllPortTypes() for other props in HardwreDevice where a port can be configured
 */
export function getHardwareDevicePortTypes(deviceConfig: DeviceConfig): PortTypeValues[] {
    const deviceInfo = getCatalogEntryByDeviceConfig(deviceConfig);
    return deviceInfo?.portTypesAvailable;
};
